/**
 * Settings → Pipeline Stages (company_admin+; the route is wrapped in
 * <RequireRole>). Sortable list, rename, colour, kind, add, delete.
 */
import { useMemo, useState } from 'react';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { errorMessage } from '@/components/Toast';
import { usePageTitle } from '@/lib/page-title';
import { useLeads, useStages } from '@/features/pipeline/api';
import type { StageKind } from '@/features/pipeline/types';
import { StageDeleteModal } from './StageDeleteModal';
import { StageRow } from './StageRow';
import { STAGE_KINDS } from './stage-colors';
import { useStageAdmin } from './use-stage-admin';

export default function StagesSettingsPage() {
  usePageTitle('Pipeline Stages');
  const stagesQ = useStages();
  const leadsQ = useLeads({});
  const stages = stagesQ.data ?? [];
  const admin = useStageAdmin(stages);
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<StageKind>('open');

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of leadsQ.data ?? []) m.set(l.stage_id, (m.get(l.stage_id) ?? 0) + 1);
    return m;
  }, [leadsQ.data]);

  const sensors = useSensors(
    // MouseSensor, not PointerSensor: the latter also fires for touch and
    // would let a swipe start a drag without the 200 ms hold (see Board.tsx).
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = stages.map((s) => s.id);
    admin.reorderTo(arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))));
  };

  return (
    <div className="max-w-[900px] px-4 py-5 sm:px-6 sm:py-6">
      <PageHeader title="Pipeline Stages" subtitle="Rename, reorder, add and remove the stages every candidate moves through." />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          admin.add(newName, newKind, stages[stages.length - 1]?.id, () => setNewName(''));
        }}
        className="mb-4 flex flex-wrap items-end gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
      >
        <label className="min-w-0 flex-1 basis-[200px] text-[12px] font-medium text-gray-700">
          New stage
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Reference check" className="mt-1 block min-h-10 w-full rounded-md border border-gray-300 px-2.5 text-sm font-normal" />
        </label>
        <label className="text-[12px] font-medium text-gray-700">
          Type
          <select value={newKind} onChange={(e) => setNewKind(e.target.value as StageKind)} className="mt-1 block min-h-10 rounded-md border border-gray-300 bg-white px-2 text-sm font-normal">
            {STAGE_KINDS.map((k) => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={!newName.trim() || admin.adding} className="inline-flex min-h-10 items-center gap-1.5 rounded-md bg-navy px-3.5 text-sm font-medium text-white hover:bg-navy-dark disabled:opacity-50">
          <Plus size={15} strokeWidth={2.2} aria-hidden /> {admin.adding ? 'Adding…' : 'Add stage'}
        </button>
      </form>

      {stagesQ.isPending ? (
        <div className="space-y-2" aria-busy="true" aria-label="Loading stages">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-white/70" />)}
        </div>
      ) : stagesQ.isError ? (
        <p role="alert" className="text-sm text-danger">Could not load stages: {errorMessage(stagesQ.error)}</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <ol className="space-y-2" aria-label="Stages in order">
              {stages.map((s, i) => (
                <StageRow key={s.id} stage={s} index={i} total={stages.length} count={counts.get(s.id) ?? 0} admin={admin} />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
      )}

      <StageDeleteModal
        stage={admin.pendingDelete?.stage ?? null}
        stages={stages}
        count={admin.pendingDelete?.count ?? 0}
        busy={admin.deleting}
        onConfirm={admin.confirmDelete}
        onClose={admin.cancelDelete}
      />
    </div>
  );
}
